import {
  Component, Injector, Input, OnDestroy, OnInit, TemplateRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import {
  Subject, takeUntil, tap
} from 'rxjs';

import { BaseControlValueAccessor, IIrisFormFieldControl } from '@sondermind/iris/form-helpers';
import { IconColors, IconSizes } from '@sondermind/iris/icon';
import { IrisIdService } from '@sondermind/iris/shared';

import { RadioOption } from './interfaces/radio-option.interface';

export enum OptionsDirection {
  ROW = 'row',
  COLUMN = 'column'
}

@Component({
  selector: 'iris-radio-group',
  templateUrl: './radio-group.component.html',
  styleUrls: ['./radio-group.component.scss'],
  host: { '[class.option-row]': 'direction === optionsDirection.ROW' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: IrisRadioGroupComponent,
    },
    IrisIdService
  ],
  standalone: false
})

export class IrisRadioGroupComponent<T>
  extends BaseControlValueAccessor
  implements OnInit, OnDestroy, IIrisFormFieldControl {
  private optionsDirection = OptionsDirection;

  @Input() canBeNull: boolean = false;
  @Input() direction: OptionsDirection = this.optionsDirection.COLUMN;
  @Input() options: Array<RadioOption<T>> = [];
  @Input() optionTemplate?: TemplateRef<{ $implicit: RadioOption<T> }>;
  uniqueId!: string;

  private currentFocusedValue: T;
  private currentHoveredValue: T;
  selectedOptionIndex = 0;
  destroyed$ = new Subject<void>();

  iconSize = IconSizes.SM;
  iconColors = IconColors;

  constructor(
    injector: Injector,
    private irisId: IrisIdService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.uniqueId = this.irisId.generate();

    const initialValueExists = this.control.value !== undefined && this.control.value !== null;
    /* Default to first value if non-nullable and the FormControl didn't initialize a value. */
    if (!initialValueExists && !this.canBeNull) {
      const firstOption = this.options[0]?.value;
      this.control.setValue(firstOption);
    }

    /* Listen to FormControl in case it gets updated programmatically */
    this.control.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        tap((newVal) => this.onSelectionChanged(newVal))
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  isSelected(optionValue: T): boolean {
    return optionValue === this.control.value;
  }

  // Note: When switching between options, the 'blur' event of the first selection
  // will always occur before the 'focus' event of the second selection.
  onBlur() {
    this.currentFocusedValue = null;
  }

  onFocus(optionValue: T): void {
    this.currentFocusedValue = optionValue;
  }

  onHoverEnter(optionValue: T): void {
    if (this.currentHoveredValue !== optionValue) {
      this.currentHoveredValue = optionValue;
    }
  }

  onHoverLeave(): void {
    this.currentHoveredValue = null;
  }

  onSelectionChanged(optionValue: T): void {
    if (optionValue !== this.control.value) {
      this.control.setValue(optionValue, { emitEvent: false });
    }
  }

  showHighlightCircle(optionValue: T): boolean {
    return this.currentFocusedValue === optionValue
      || this.currentHoveredValue === optionValue;
  }

  get value(): T {
    return this.control.value;
  }

  override get control(): FormControl {
    return this.formControl || this.controlContainer.control.get(this.formControlName) as FormControl;
  }
}
